import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './TripPlanner.module.scss';
import { Map, Plus, X, Users, Star, MapPin, GripVertical, Calendar, Check, ExternalLink } from 'lucide-react';
import { getLocais, getRoutes, saveRoute, SavedRoute } from '../../services/api.tsx';

// ─────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────
interface Establishment {
  id: string;
  nome: string;
  tipo: string;
  cidade: string;
  avaliacao: number;
  imagem: string;
  tempo: number;
  preco: number | string;
  latitude?: number;
  longitude?: number;
  endereco?: string;
}

interface TimelineStep {
  id: string;
  time: string;
  est: Establishment;
}

interface DayRoute {
  id: string;
  dayNumber: number;
  date: string;
  steps: TimelineStep[];
}

// ─────────────────────────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────────────────────────
const ESTABLISHMENTS: Establishment[] = [
  { id: 'e1',  nome: 'Casa da Feijoada',         tipo: 'Restaurante', cidade: 'Aracaju',        avaliacao: 4.7, imagem: '/assets/restaurante-feijoada.jpg',        tempo: 90,  preco: 45 },
  { id: 'e2',  nome: 'Mandacaru',                tipo: 'Restaurante', cidade: 'Canindé',         avaliacao: 4.5, imagem: '/assets/restaurante-mandacaru.jpg',       tempo: 120, preco: 60 },
  { id: 'e3',  nome: 'Hotel Beira-Mar',           tipo: 'Hotel',       cidade: 'Aracaju',        avaliacao: 4.6, imagem: '/assets/hotel-beira-mar.jpg',             tempo: 0,   preco: 350 },
  { id: 'e4',  nome: 'Pousada Lua Nova',          tipo: 'Pousada',     cidade: 'Aracaju',        avaliacao: 4.8, imagem: '/assets/hotel-pousada-lua-nova.jpg',      tempo: 0,   preco: 220 },
  { id: 'e5',  nome: 'Pousada Xingó',             tipo: 'Pousada',     cidade: 'Canindé',        avaliacao: 4.8, imagem: '/assets/hotel-xingo.jpg',                 tempo: 0,   preco: 280 },
  { id: 'e6',  nome: 'Cantina Italiana',          tipo: 'Restaurante', cidade: 'Aracaju',        avaliacao: 4.0, imagem: '/assets/restaurante-cantina.jpg',        tempo: 90,  preco: 85 },
  { id: 'e7',  nome: 'Sabor Nordestino',          tipo: 'Restaurante', cidade: 'São Cristóvão', avaliacao: 4.8, imagem: '/assets/restaurante-sabor-nordestino.jpg',tempo: 80,  preco: 50 },
  { id: 'e8',  nome: 'Tempero do Agreste',        tipo: 'Restaurante', cidade: "Itaporanga",     avaliacao: 4.9, imagem: '/assets/restaurante-tempero-agreste.jpg', tempo: 70,  preco: 55 },
  { id: 'e9',  nome: 'Noriko Sushi',              tipo: 'Restaurante', cidade: 'Itabaiana',      avaliacao: 4.0, imagem: '/assets/restaurante-sushi.jpg',           tempo: 100, preco: 95 },
  { id: 'e10', nome: 'Hotel Villa Nova',           tipo: 'Hotel',       cidade: 'Barra dos Coqueiros', avaliacao: 4.3, imagem: '/assets/hotel-villa-nova.jpg',   tempo: 0,   preco: 300 },
  { id: 'e11', nome: 'Pousada Sheik',              tipo: 'Pousada',     cidade: 'Itabaiana',     avaliacao: 4.0, imagem: '/assets/hotel-sheik.jpg',                tempo: 0,   preco: 150 },
  { id: 'e12', nome: 'Hotel Palace',               tipo: 'Hotel',       cidade: "Itaporanga",    avaliacao: 4.9, imagem: '/assets/hotel-palace.jpg',               tempo: 0,   preco: 240 },
  { id: 'e13', nome: 'Museu da Gente Sergipana',   tipo: 'Entretenimento', cidade: 'Aracaju',     avaliacao: 4.9, imagem: '/assets/museu-gente-sergipana.jpg',      tempo: 120, preco: 0 },
  { id: 'e14', nome: 'Orla de Atalaia',            tipo: 'Entretenimento', cidade: 'Aracaju',     avaliacao: 4.8, imagem: '/assets/orla-atalaia.jpg',               tempo: 180, preco: 0 },
  { id: 'e15', nome: 'Parque dos Falcões',         tipo: 'Entretenimento', cidade: 'Itabaiana',   avaliacao: 4.7, imagem: '/assets/parque-falcoes.jpg',             tempo: 150, preco: 40 },
];

const CATEGORIES = ['Todos', 'Hospedagem', 'Restaurante', 'Entretenimento'];

// Alturas variadas para efeito masonry autêntico (simula imagens em proportion diferentes)
const HEIGHTS: Record<string, number> = {
  e1: 220, e2: 165, e3: 260, e4: 200, e5: 180,
  e6: 240, e7: 170, e8: 210, e9: 195, e10: 230,
  e11: 180, e12: 250, e13: 190, e14: 250, e15: 220,
};

// Helper para gerar tempo baseado nos anteriores
function computeTime(steps: TimelineStep[], newEst: Establishment): string {
  if (steps.length === 0) return '09:00';
  const last = steps[steps.length - 1];
  const [h, m] = last.time.split(':').map(Number);
  const total = h * 60 + m + last.est.tempo + 30;
  const newH = Math.floor(total / 60) % 24;
  const newM = total % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function TripPlanner() {
  const navigate = useNavigate();

  const [activeDayId, setActiveDayId] = useState<string>('d1');
  const [activeCategory, setActiveCategory] = useState<string>('Todos');
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isRouteConfirmed, setIsRouteConfirmed] = useState(false);
  const [establishments, setEstablishments] = useState<Establishment[]>(ESTABLISHMENTS);
  const [isSavingRoute, setIsSavingRoute] = useState(false);
  const [routes, setRoutes] = useState<DayRoute[]>(getInitialRoutes());
  const [savedRoutes, setSavedRoutes] = useState<SavedRoute[]>([]);
  const [selectedSavedRoute, setSelectedSavedRoute] = useState<SavedRoute | null>(null);

  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('tripPlannerTutorialSeen');
    if (!hasSeenTutorial) {
      setShowTutorial(true);
    }
  }, []);

  const normalizeLocal = (local: any): Establishment => {
    const rawCategoria = local.categoria;
    let tipo = 'Outros';
    if (typeof rawCategoria === 'string') {
      if (rawCategoria.toLowerCase().includes('hotel') || rawCategoria.toLowerCase().includes('pousada')) tipo = 'Hotel';
      else if (rawCategoria.toLowerCase().includes('restaurante')) tipo = 'Restaurante';
      else if (rawCategoria.toLowerCase().includes('entretenimento') || rawCategoria.toLowerCase().includes('praia') || rawCategoria.toLowerCase().includes('museu')) tipo = 'Entretenimento';
    }
    if (Array.isArray(rawCategoria)) {
      const joined = rawCategoria.map((item: any) => item?.nomeCategoria || item?.nome).join(' ').toLowerCase();
      if (joined.includes('hotel') || joined.includes('pousada')) tipo = 'Hotel';
      else if (joined.includes('restaurante') || joined.includes('comida')) tipo = 'Restaurante';
      else if (joined.includes('praia') || joined.includes('museu') || joined.includes('entretenimento')) tipo = 'Entretenimento';
    }

    const cidade = typeof local.cidade === 'string'
      ? local.cidade
      : typeof local.endereco === 'string'
      ? local.endereco.split(',')[1]?.trim() || 'Sergipe'
      : 'Sergipe';

    return {
      id: String(local.id),
      nome: local.nome || 'Local sem nome',
      tipo,
      cidade,
      avaliacao: Number(local.avaliacao ?? 0),
      imagem: Array.isArray(local.fotos) && local.fotos.length ? local.fotos[0] : local.imagem || '/assets/placeholder.png',
      tempo: typeof local.tempo === 'number' ? local.tempo : tipo === 'Hotel' ? 0 : 90,
      preco: local.mediaPreco || '—',
      endereco: local.endereco,
      latitude: Number(local.latitude ?? local.lat),
      longitude: Number(local.longitude ?? local.lng),
    };
  };

  useEffect(() => {
    const loadEstablishments = async () => {
      try {
        const locais = await getLocais();
        const mapped = locais.map(normalizeLocal);
        setEstablishments(mapped);
      } catch (error) {
        console.error('Falha ao carregar estabelecimentos:', error);
      }
    };
    loadEstablishments();
  }, []);

  function formatShortDate(date: Date) {
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${String(date.getDate()).padStart(2, '0')} ${months[date.getMonth()]}`;
  }

  function getInitialRoutes(): DayRoute[] {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return [
      { id: 'd1', dayNumber: 1, date: formatShortDate(today), steps: [] },
      { id: 'd2', dayNumber: 2, date: formatShortDate(tomorrow), steps: [] },
    ];
  }

  const closeTutorial = () => {
    localStorage.setItem('tripPlannerTutorialSeen', 'true');
    setShowTutorial(false);
  };

  useEffect(() => {
    const loadSavedRoutes = async () => {
      try {
        const routes = await getRoutes();
        setSavedRoutes(routes);
      } catch (error) {
        const localValue = localStorage.getItem('@Sergipanidade:lastRoute');
        if (localValue) {
          try {
            setSavedRoutes([JSON.parse(localValue) as SavedRoute]);
          } catch {
            setSavedRoutes([]);
          }
        }
      }
    };
    loadSavedRoutes();
  }, []);

  const loadSavedRouteIntoPlanner = (route: SavedRoute) => {
    const mappedSteps: TimelineStep[] = route.steps.map((step) => {
      const matchedEst = establishments.find(est => Number(est.id) === step.localId);
      return {
        id: step.id,
        time: step.time,
        est: {
          id: String(step.localId),
          nome: step.nome,
          tipo: step.tipo,
          cidade: step.cidade,
          avaliacao: matchedEst?.avaliacao ?? 0,
          imagem: matchedEst?.imagem ?? step.imagem ?? '/assets/placeholder.png',
          tempo: matchedEst?.tempo ?? 0,
          preco: matchedEst?.preco ?? '—',
          latitude: step.latitude,
          longitude: step.longitude,
          endereco: step.endereco,
        },
      };
    });

    setRoutes([{ id: `r${route.dayNumber}`, dayNumber: route.dayNumber, date: route.date, steps: mappedSteps }]);
    setActiveDayId(`r${route.dayNumber}`);
    setSelectedSavedRoute(null);
  };

  // ─── Drag state from grid ────────────────────────────────────────
  const draggingFromGrid = useRef<Establishment | null>(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);

  // ─── Custom Drag Ghost ──────────────────────────────────────────
  const ghostRef = useRef<HTMLDivElement>(null);
  const ghostInitialPos = useRef({ x: 0, y: 0 });
  const emptyDragImage = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    emptyDragImage.current = img;
  }, []);

  // ─── Drag state inside timeline (reorder) ────────────────────────
  const draggingTimelineId = useRef<string | null>(null);
  const [dragOverTimelineId, setDragOverTimelineId] = useState<string | null>(null);
  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);

  // ─── Active route ─────────────────────────────────────────────────
  const activeRoute = routes.find(r => r.id === activeDayId)!;

  // ─── Filtered establishments ──────────────────────────────────────
  const filteredEstablishments = establishments.filter(est => {
    if (activeCategory === 'Todos') return true;
    if (activeCategory === 'Hospedagem') return est.tipo === 'Hotel' || est.tipo === 'Pousada';
    if (activeCategory === 'Restaurante') return est.tipo === 'Restaurante';
    if (activeCategory === 'Entretenimento') return est.tipo === 'Entretenimento';
    return true;
  });

  // ─── Add step ─────────────────────────────────────────────────────
  const addStep = useCallback((est: Establishment) => {
    setRoutes(prev => prev.map(route => {
      if (route.id !== activeDayId) return route;
      const newTime = computeTime(route.steps, est);
      return {
        ...route,
        steps: [...route.steps, { id: `step_${Date.now()}`, time: newTime, est }]
      };
    }));
  }, [activeDayId]);

  const persistRouteLocally = (route: SavedRoute) => {
    try {
      localStorage.setItem('@Sergipanidade:lastRoute', JSON.stringify(route));
    } catch {
      // ignore storage failure
    }
  };

  const handleSaveRoute = async (route: DayRoute) => {
    const payload: SavedRoute = {
      dayNumber: route.dayNumber,
      date: route.date,
      createdAt: new Date().toISOString(),
      steps: route.steps.map((step) => ({
        id: step.id,
        time: step.time,
        localId: Number(step.est.id),
        nome: step.est.nome,
        tipo: step.est.tipo,
        cidade: step.est.cidade,
        endereco: step.est.endereco,
        latitude: step.est.latitude,
        longitude: step.est.longitude,
        imagem: step.est.imagem,
      })),
    };

    setIsSavingRoute(true);
    try {
      await saveRoute(payload);
    } catch (_error) {
      // se o backend não suportar, salvamos localmente para exibição imediata
    } finally {
      persistRouteLocally(payload);
      setIsSavingRoute(false);
    }
  };

  // ─── Remove step ──────────────────────────────────────────────────
  const removeStep = (stepId: string) => {
    setRoutes(prev => prev.map(r =>
      r.id === activeDayId
        ? { ...r, steps: r.steps.filter(s => s.id !== stepId) }
        : r
    ));
  };

  // ─── Clear Timeline ───────────────────────────────────────────────
  const clearTimeline = () => {
    setIsClearing(true);
    setTimeout(() => {
      setRoutes(prev => prev.map(r =>
        r.id === activeDayId ? { ...r, steps: [] } : r
      ));
      setIsClearing(false);
    }, 350); // duração compatível com a animação CSS
  };

  // ─── DRAG FROM GRID: handlers ────────────────────────────────────
  const onGridDragStart = (e: React.DragEvent, est: Establishment) => {
    draggingFromGrid.current = est;
    e.dataTransfer.effectAllowed = 'copy';
    setDraggingCardId(est.id);
    
    if (emptyDragImage.current) {
      e.dataTransfer.setDragImage(emptyDragImage.current, 0, 0);
    }
    
    ghostInitialPos.current = { x: e.clientX, y: e.clientY };
  };

  const onGridDrag = (e: React.DragEvent) => {
    if (e.clientX === 0 && e.clientY === 0) return;
    if (ghostRef.current) {
      ghostRef.current.style.transform = `translate(${e.clientX + 15}px, ${e.clientY + 15}px) rotate(3deg)`;
    }
  };

  const onGridDragEnd = () => {
    draggingFromGrid.current = null;
    setDropZoneActive(false);
    setDraggingCardId(null);
  };

  // Sidebar as drop target
  const sidebarRef = useRef<HTMLElement | null>(null);

  const onSidebarDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (draggingFromGrid.current) setDropZoneActive(true);
  };

  const onSidebarDragLeave = (e: React.DragEvent) => {
    // Only deactivate if the mouse truly left the sidebar (not just moved to a child)
    if (sidebarRef.current && e.relatedTarget instanceof Node && sidebarRef.current.contains(e.relatedTarget)) {
      return;
    }
    setDropZoneActive(false);
  };

  const onSidebarDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDropZoneActive(false);
    // Only handle drops from the grid (not timeline reorders)
    if (draggingFromGrid.current) {
      addStep(draggingFromGrid.current);
      draggingFromGrid.current = null;
    }
  };

  // ─── DRAG WITHIN TIMELINE: handlers ─────────────────────────────
  const onTimelineDragStart = (e: React.DragEvent, stepId: string) => {
    e.stopPropagation(); // Don't bubble to sidebar drop handler
    draggingTimelineId.current = stepId;
    setDraggingCardId(stepId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onTimelineDragOver = (e: React.DragEvent, stepId: string) => {
    e.preventDefault();
    // If dragging from grid, let the event bubble to the sidebar drop handler
    if (draggingFromGrid.current) return;
    e.stopPropagation();
    if (draggingTimelineId.current && draggingTimelineId.current !== stepId) {
      setDragOverTimelineId(stepId);
    }
  };

  const onTimelineDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    // If dragging from grid, let the sidebar handle it
    if (draggingFromGrid.current) return;
    e.stopPropagation();
    const sourceId = draggingTimelineId.current;
    if (!sourceId || sourceId === targetId) return;

    setRoutes(prev => prev.map(r => {
      if (r.id !== activeDayId) return r;
      const steps = [...r.steps];
      const srcIdx = steps.findIndex(s => s.id === sourceId);
      const tgtIdx = steps.findIndex(s => s.id === targetId);
      if (srcIdx === -1 || tgtIdx === -1) return r;
      const [moved] = steps.splice(srcIdx, 1);
      steps.splice(tgtIdx, 0, moved);
      return { ...r, steps };
    }));

    draggingTimelineId.current = null;
    setDragOverTimelineId(null);
    setDraggingCardId(null);
  };

  const onTimelineDragEnd = () => {
    draggingTimelineId.current = null;
    setDragOverTimelineId(null);
    setDraggingCardId(null);
  };

  return (
    <div className={styles.container}>

      {/* ── SIDEBAR ─────────────────────────────────────────── */}
      <aside
        ref={sidebarRef}
        className={styles.sidebar}
        onDragOver={onSidebarDragOver}
        onDragLeave={onSidebarDragLeave}
        onDrop={onSidebarDrop}
      >
        {/* Header */}
        <div className={styles.sidebarHeader}>
          <div className={styles.sidebarTitleRow}>
            <h1>Meu Roteiro</h1>
            {activeRoute.steps.length > 1 && (
              <button 
                className={styles.clearRouteBtn} 
                onClick={clearTimeline}
                title="Limpar roteiro do dia"
              >
                Limpar
              </button>
            )}
          </div>
          <p className={styles.sidebarSubtitle}>
            Arraste locais para cá ou adicione pelo botão.
          </p>

          {/* Friends row */}
          <div className={styles.friendsRow}>
            <div className={styles.avatarStack}>
              <div className={styles.avatar}>EU</div>
              <div className={styles.avatar} style={{ background: '#4A7FD6' }}>MB</div>
              <div className={styles.avatar} style={{ background: '#7C3AED' }}>JP</div>
            </div>
            <button className={styles.inviteBtn} onClick={() => setIsInviteOpen(true)}>
              <Plus size={12} strokeWidth={3} /> Convidar
            </button>
          </div>
        </div>

        {/* Day Tabs */}
        <div className={styles.dayTabs}>
          {routes.map(r => (
            <button
              key={r.id}
              className={`${styles.dayTab} ${activeDayId === r.id ? styles.activeTab : ''}`}
              onClick={() => setActiveDayId(r.id)}
            >
              Dia {r.dayNumber} · {r.date}
            </button>
          ))}
        </div>

        {/* Saved Routes */}
        <div className={styles.savedRoutesSection}>
          <div className={styles.savedRoutesHeader}>
            <h3>Roteiros salvos</h3>
          </div>
          {savedRoutes.length > 0 ? (
            <div className={styles.savedRoutesList}>
              {savedRoutes.map(route => (
                <button
                  key={`${route.dayNumber}-${route.createdAt}`}
                  className={styles.savedRouteButton}
                  onClick={() => setSelectedSavedRoute(route)}
                >
                  <span>Dia {route.dayNumber} · {route.date}</span>
                  <small>{route.steps.length} local{route.steps.length === 1 ? '' : 's'}</small>
                </button>
              ))}
            </div>
          ) : (
            <p className={styles.savedRoutesEmpty}>Nenhum roteiro salvo ainda.</p>
          )}
        </div>

        {/* Timeline body */}
        <div className={styles.timelineBody}>
          {/* Drop zone flash */}
          {dropZoneActive && (
            <div className={styles.timelineDropZone}>
              ✦ Solte aqui para adicionar ao Dia {activeRoute.dayNumber}
            </div>
          )}

          {activeRoute.steps.length === 0 && !dropZoneActive ? (
            <div className={styles.emptyTimeline}>
              <Calendar size={40} className={styles.emptyIcon} />
              <h3>Nenhum local ainda</h3>
              <p>Arraste um card da direita ou clique em "Adicionar" nos cards.</p>
            </div>
          ) : (
            <div className={`${styles.timelineList} ${isClearing ? styles.isClearing : ''}`}>
              {activeRoute.steps.map((step, idx) => (
                <div
                  key={step.id}
                  className={`
                    ${styles.timelineItem}
                    ${draggingCardId === step.id ? styles.dragging : ''}
                    ${dragOverTimelineId === step.id ? styles.dragOver : ''}
                  `}
                  draggable
                  onDragStart={e => onTimelineDragStart(e, step.id)}
                  onDragOver={e => onTimelineDragOver(e, step.id)}
                  onDrop={e => onTimelineDrop(e, step.id)}
                  onDragEnd={onTimelineDragEnd}
                >
                  {/* Time slot and bullet */}
                  <div className={styles.timelineTimeLabel}>{step.time}</div>
                  <div className={styles.timelineBullet} />

                  {/* TIMELINE CARD */}
                  <div className={styles.timelineCard}>
                    <img
                      className={styles.timelineCardImage}
                      src={step.est.imagem}
                      alt={step.est.nome}
                    />
                    {/* Gradient bleed from photo → white */}
                    <div className={styles.timelineCardGradient} />

                    <div className={styles.timelineCardBody}>
                      <div className={styles.timelineCardInfo}>
                        <p className={styles.tcName}>{step.est.nome}</p>
                        <div className={styles.tcMeta}>
                          <span className={styles.tcType}>{step.est.tipo}</span>
                          <span>·</span>
                          <MapPin size={11} />
                          <span>{step.est.cidade}</span>
                          <span>·</span>
                          <Star size={11} fill="#FFD60A" color="#FFD60A" />
                          <span>{step.est.avaliacao}</span>
                        </div>
                      </div>
                      <div className={styles.tcActions}>
                        <button
                          className={styles.tcRemoveBtn}
                          onClick={() => removeStep(step.id)}
                          title="Remover"
                        >
                          <X size={12} strokeWidth={3} />
                        </button>
                        <div className={styles.tcDragHandle} title="Arraste para reordenar">
                          <GripVertical size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {!dropZoneActive && (
                <div className={styles.explicitDropZone}>
                  <Plus size={16} /> Arraste um local aqui
                </div>
              )}
            </div>
          )}

          {/* Finalizar Rota */}
          <button 
            className={styles.finishRouteBtn} 
            disabled={activeRoute.steps.length === 0}
            onClick={() => setIsFinishModalOpen(true)}
          >
            Finalizar Rota
          </button>
        </div>
      </aside>

      {/* ── EXPLORE AREA ────────────────────────────────────── */}
      <main className={styles.exploreArea}>

        {/* Top bar */}
        <div className={styles.exploreHeader}>
          <h2>Explorar Locais</h2>
          <button className={styles.mapNavBtn} onClick={() => navigate('/mapa')}>
            <Map size={16} />
            Ver no Mapa
          </button>
        </div>

        {/* Categories Filter */}
        <div className={styles.categoryFilter}>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`${styles.filterBtn} ${activeCategory === cat ? styles.activeFilter : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* MASONRY PINTEREST GRID */}
        <div className={styles.masonryGrid}>
          {filteredEstablishments.map(est => (
            <div
              key={est.id}
              className={`${styles.pinterestCard} ${draggingCardId === est.id ? styles.isDraggingActive : ''}`}
              draggable
              onDragStart={e => onGridDragStart(e, est)}
              onDrag={onGridDrag}
              onDragEnd={onGridDragEnd}
              title={`Arraste para adicionar ao roteiro: ${est.nome}`}
            >
              <div className={styles.pcImageWrapper}>
                <img
                  src={est.imagem}
                  alt={est.nome}
                  style={{ height: `${HEIGHTS[est.id] ?? 200}px`, objectFit: 'cover' }}
                />
                <button 
                  className={styles.pcAddBtn} 
                  onClick={(e) => { e.stopPropagation(); addStep(est); }}
                  title="Adicionar ao roteiro"
                >
                  <Plus size={18} strokeWidth={3} />
                </button>
              </div>
              <div className={styles.pcMetaContainer}>
                <p className={styles.pcTitle}>{est.nome}</p>
                <div className={styles.pcDetails}>
                  <span className={styles.pcLocation}><MapPin size={11} /> {est.cidade}</span>
                  <span className={styles.pcRating}><Star size={11} fill="#FFD60A" color="#FFD60A" /> {est.avaliacao}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* ── INVITE MODAL ─────────────────────────────────────── */}
      {isInviteOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsInviteOpen(false)}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <button className={styles.modalCloseBtn} onClick={() => setIsInviteOpen(false)}>
              <X size={16} />
            </button>
            <Users size={36} color="#E86C2C" />
            <h3>Viaje com a galera!</h3>
            <p>
              Convide amigos para co-editar e visualizar este roteiro em tempo real.
            </p>
            <div className={styles.inviteInputRow}>
              <input
                type="email"
                placeholder="E-mail do amigo..."
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                autoFocus
              />
              <button
                onClick={() => {
                  if (inviteEmail) {
                    alert(`Convite enviado para ${inviteEmail}!`);
                    setInviteEmail('');
                    setIsInviteOpen(false);
                  }
                }}
              >
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TUTORIAL MODAL ─────────────────────────────────────── */}
      {showTutorial && (
        <div className={styles.modalOverlay} onClick={closeTutorial}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <button className={styles.modalCloseBtn} onClick={closeTutorial}>
              <X size={16} />
            </button>
            <div className={styles.tutorialIcon}>
              <Map size={36} color="#E86C2C" />
            </div>
            <h3>Bem-vindo ao Planejador de Roteiro!</h3>
            <p>
              Agora você pode organizar sua viagem de forma visual e interativa.
            </p>
            <ul className={styles.tutorialList}>
              <li><strong>Descubra:</strong> Explore a nossa seleção de locais à direita.</li>
              <li><strong>Arraste:</strong> Segure e arraste os locais que você mais gostou para a barra lateral do seu roteiro.</li>
              <li><strong>Organize:</strong> Você pode reordenar os locais do roteiro apenas arrastando eles para cima ou para baixo.</li>
            </ul>
            <button className={styles.tutorialBtn} onClick={closeTutorial}>
              Começar a Planejar!
            </button>
          </div>
        </div>
      )}

      {/* ── CUSTOM DRAG GHOST ────────────────────────────────────── */}
      {draggingFromGrid.current && draggingCardId === draggingFromGrid.current.id && (
        <div
          ref={ghostRef}
          className={styles.customDragGhost}
          style={{
            transform: `translate(${ghostInitialPos.current.x + 15}px, ${ghostInitialPos.current.y + 15}px) rotate(3deg)`
          }}
        >
          <div className={styles.pcImageWrapper}>
            <img
              src={draggingFromGrid.current.imagem}
              alt={draggingFromGrid.current.nome}
              style={{ height: `${HEIGHTS[draggingFromGrid.current.id] ?? 200}px`, objectFit: 'cover' }}
            />
          </div>
          <div className={styles.pcMetaContainer}>
            <p className={styles.pcTitle}>{draggingFromGrid.current.nome}</p>
            <div className={styles.pcDetails}>
              <span className={styles.pcLocation}><MapPin size={11} /> {draggingFromGrid.current.cidade}</span>
              <span className={styles.pcRating}><Star size={11} fill="#FFD60A" color="#FFD60A" /> {draggingFromGrid.current.avaliacao}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── CONFIRM ROUTE MODAL ────────────────────────────────────── */}
      {isFinishModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsFinishModalOpen(false)}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <button className={styles.modalCloseBtn} onClick={() => setIsFinishModalOpen(false)}>
              <X size={16} />
            </button>
            <Map size={36} color="#E86C2C" style={{ marginBottom: '12px' }} />
            <h3>Rota do Dia {activeRoute.dayNumber}</h3>
            <p>Confira os estabelecimentos selecionados para o seu roteiro.</p>
            
            <div className={styles.confirmRouteList}>
              {activeRoute.steps.map((step) => (
                <div key={step.id} className={styles.confirmRouteItem}>
                  <img src={step.est.imagem} alt={step.est.nome} />
                  <div className={styles.itemInfo}>
                    <h4>{step.est.nome}</h4>
                    <p><MapPin size={12} /> {step.est.cidade} • {step.time}</p>
                  </div>
                  <button className={styles.itemLink} onClick={() => navigate('/estabelecimentos', { state: { busca: step.est.nome } })}>
                    <ExternalLink size={12} /> Ver Local
                  </button>
                </div>
              ))}
            </div>

            <div className={styles.confirmActions}>
              <button className={styles.btnMap} onClick={() => {
                navigate('/mapa');
              }}>
                <Map size={16} /> Ver no Mapa
              </button>
              <button className={styles.btnConfirm} onClick={async () => {
                await handleSaveRoute(activeRoute);
                setIsRouteConfirmed(true);
                setTimeout(() => {
                  setIsRouteConfirmed(false);
                  setIsFinishModalOpen(false);
                }, 2800);
              }} disabled={isSavingRoute || activeRoute.steps.length === 0}>
                {isSavingRoute ? 'Salvando...' : 'Confirmar Rota'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUCCESS OVERLAY ────────────────────────────────────── */}
      {selectedSavedRoute && (
        <div className={styles.modalOverlay} onClick={() => setSelectedSavedRoute(null)}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <button className={styles.modalCloseBtn} onClick={() => setSelectedSavedRoute(null)}>
              <X size={16} />
            </button>
            <Map size={36} color="#4A90E2" style={{ marginBottom: '12px' }} />
            <h3>Roteiro do Dia {selectedSavedRoute.dayNumber}</h3>
            <p>Visualize o roteiro salvo e carregue-o no planner.</p>

            <div className={styles.confirmRouteList}>
              {selectedSavedRoute.steps.map(step => (
                <div key={step.id} className={styles.confirmRouteItem}>
                  <div className={styles.itemInfo}>
                    <h4>{step.nome}</h4>
                    <p><MapPin size={12} /> {step.cidade} • {step.time}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.confirmActions}>
              <button className={styles.btnMap} onClick={() => setSelectedSavedRoute(null)}>
                Fechar
              </button>
              <button className={styles.btnConfirm} onClick={() => loadSavedRouteIntoPlanner(selectedSavedRoute)}>
                Carregar no planner
              </button>
            </div>
          </div>
        </div>
      )}

      {isRouteConfirmed && (
        <div className={styles.successOverlay}>
          <div className={styles.successContent}>
            <div className={styles.successCircle}>
              <Check size={48} strokeWidth={3} className={styles.successCheck} />
            </div>
            <h2 className={styles.successTitle}>Rota Confirmada!</h2>
            <p className={styles.successSubtitle}>Boa viagem! Seu roteiro está salvo.</p>
          </div>
        </div>
      )}

    </div>
  );
}
